import type {
  HubDatapoolView,
  HubJob,
  HubJobKind,
  HubTaskArtifactBindings,
  HubTaskDependencyBindings,
  HubWorkflowTask,
} from '@robovision/hub'
import { mapToObject } from '@robovision/rvai-utils'
import type { JobResources } from '../algorithm/jobDefinitions/common/JobResources'


export interface WorkflowTaskDependencies {
  blobs?: Record<string, string | WorkflowTask>
  datapools?: Record<string, HubDatapoolView | WorkflowTask>
  datasets?: Record<string, (string | WorkflowTask)[]>
  models?: Record<string, string | WorkflowTask>
  parameters?: Record<string, string | WorkflowTask>
}

export interface WorkflowTaskParams {
  algorithmId: string
  algorithmVersion: string
  artifacts: HubTaskArtifactBindings
  definitionId: string
  dependencies: WorkflowTaskDependencies
  id: string
  job?: HubJob
  kind: HubJobKind
  name: string
  runtimeParameters: JobResources
}

export class WorkflowTask {
  public algorithmId: string
  public algorithmVersion: string
  public artifacts: HubTaskArtifactBindings
  public definitionId: string
  public dependencies: WorkflowTaskDependencies
  public id: string
  public job?: HubJob
  public kind: HubJobKind
  public name: string
  public runtimeParameters: JobResources

  constructor (params: WorkflowTaskParams) {
    this.algorithmId = params.algorithmId
    this.algorithmVersion = params.algorithmVersion
    this.artifacts = params.artifacts
    this.definitionId = params.definitionId
    this.dependencies = params.dependencies
    this.id = params.id
    this.job = params.job
    this.kind = params.kind
    this.name = params.name
    this.runtimeParameters = params.runtimeParameters
  }

  static buildDependencies (
    bindings: HubTaskDependencyBindings,
    tasks: WorkflowTask[],
  ): WorkflowTaskDependencies {
    // TODO can this method be reduced somehow?
    const dependencies: WorkflowTaskDependencies = {}
    if (bindings.blobs) {
      dependencies.blobs = mapToObject(
        bindings.blobs,
        x => {
          if (x.startsWith('$')) {
            const dep = tasks.find(t => Object.values(t.artifacts.blobs ?? {}).includes(x))
            if (!dep) throw new Error('Missing dependency')
            return dep
          } else {
            return x
          }
        },
      )
    }
    if (bindings.datapools) {
      dependencies.datapools = mapToObject(
        bindings.datapools,
        x => {
          if (typeof x === 'string') {
            const dep = tasks.find(t => Object.values(t.artifacts.datapools ?? {}).includes(x))
            if (!dep) throw new Error('Missing dependency')
            return dep
          } else {
            return x
          }
        },
      )
    }
    if (bindings.datasets) {
      dependencies.datasets = mapToObject(
        bindings.datasets,
        arr => arr.map(
          x => {
            if (x.startsWith('$')) {
              let dep = tasks.find(t => Object.values(t.artifacts.examples ?? {}).flat().includes(x))
              if (!dep) {
                dep = tasks.find(t => Object.values(t.artifacts.annotations ?? {}).flat().includes(x))
              }
              if (!dep) {
                dep = tasks.find(t => Object.values(t.artifacts.datasets ?? {}).flat().includes(x))
              }
              if (!dep) throw new Error('Missing dependency')
              return dep
            } else {
              return x
            }
          },
        ),
      )
    }
    if (bindings.models) {
      dependencies.models = mapToObject(
        bindings.models,
        x => {
          if (x.startsWith('$')) {
            const dep = tasks.find(t => Object.values(t.artifacts.models ?? {}).includes(x))
            if (!dep) throw new Error('Missing dependency')
            return dep
          } else {
            return x
          }
        },
      )
    }
    if (bindings.parameters) {
      dependencies.parameters = mapToObject(
        bindings.parameters,
        x => {
          if (x.startsWith('$')) {
            const dep = tasks.find(t => Object.values(t.artifacts.parameters ?? {}).includes(x))
            if (!dep) throw new Error('Missing dependency')
            return dep
          } else {
            return x
          }
        },
      )
    }

    return dependencies
  }

  public static buildResources (task: HubWorkflowTask): JobResources {
    const mainResources = task.runtimeParameters?.resources ?? {}

    return {
      main: {
        gpu: mainResources.limits?.gpu != null
          ? mainResources.limits?.gpu
          : undefined,
      },
    }
  }

  static fromResponse (
    response: HubWorkflowTask,
    tasks: WorkflowTask[],
  ): WorkflowTask {
    return new WorkflowTask({
      algorithmId: response.algorithmId,
      algorithmVersion: response.algorithmVersion,
      artifacts: response.artifacts,
      definitionId: response.definitionId,
      dependencies: WorkflowTask.buildDependencies(response.dependencies, tasks),
      id: response.id,
      job: response.job,
      kind: response.kind,
      name: response.name,
      runtimeParameters: WorkflowTask.buildResources(response),
    })
  }
}
